import { Component, Input } from '@angular/core';
import { CLESummaryRowDTO } from '../../../CLE/_core/models/cle-check.models';

@Component({
  selector: 'app-cle-summary-table',
  templateUrl: './cle-summary-table.component.html',
  styleUrls: ['./cle-summary-table.component.scss']
})
export class CleSummaryTableComponent {
  @Input() title: string = 'Summary';
  @Input() rows: CLESummaryRowDTO[] = [];
  @Input() showInHouse: boolean = false; // If true, show 4-column format with In-House
  @Input() headingMode: 'cle' | 'regular' = 'cle';
  @Input() compact: boolean = false;

  /**
   * TRUE while the period is still running. An open period's "Hours Needed" is hours REMAINING, not
   * hours owed, and must not be emphasised as a shortfall. Defaults false so a caller that does not
   * pass it behaves as a closed period — the conservative side for a CLOSED-period table, and the
   * caller that shows an open period does pass it.
   */
  @Input() periodIsOpen: boolean = false;

  /**
   * ⚠ ROWS ARE IDENTIFIED BY summaryLine, NEVER BY sortOrder.
   *
   * These lists were keyed on sortOrder — [9, 11] hidden, [1, 2, 8] always shown — and that is not a
   * stable identity. The backend numbers the rows it EMITTED (CleOnePassSummaryRowMapper assigns
   * `SortOrder = i + 1` after building the list) and it skips conditional rows, so position 9 is
   * whichever row happens to be ninth FOR THAT MEMBER. One attorney's 9 was the cumulative line and
   * another's was something else entirely, so this hid and force-showed arbitrary rows, per member,
   * on a member-facing page. The employee portal hit the same defect and fixed it the same way.
   *
   * summaryLine is the backend's stable logic key (CleHoursSummaryLineBuilder). Compared EXACTLY:
   * "Hours Needed" is a PREFIX of both "Hours Needed Cumulative" and "Hours Needed Prev Year", so a
   * substring or startsWith test silently matches three different rows.
   */
  private static readonly LINE = {
    totalCle: 'Total CLE',
    required: 'Hours Required',
    needed: 'Hours Needed',
    neededPrevYear: 'Hours Needed Prev Year',
    neededCumulative: 'Hours Needed Cumulative',
    lost: 'Hours Lost',
  } as const;

  // Lines hidden at CLE's request (2026-07) to reduce clutter. Kept in the data — the compliance
  // verdict still reads the cumulative line — and merely filtered from display, so they come back by
  // removing the entry.
  private readonly hiddenLines: string[] = [
    CleSummaryTableComponent.LINE.neededCumulative,
    CleSummaryTableComponent.LINE.lost,
  ];

  // Always shown even when every value is zero, because their absence is itself information: what the
  // period requires, what the member has, and what is left.
  private readonly alwaysShownLines: string[] = [
    CleSummaryTableComponent.LINE.totalCle,
    CleSummaryTableComponent.LINE.required,
    CleSummaryTableComponent.LINE.needed,
  ];

  private static key(row: CLESummaryRowDTO): string {
    return (row.summaryLine || '').trim();
  }

  get displayRows(): CLESummaryRowDTO[] {
    return (this.rows || []).filter(row => {
      const line = CleSummaryTableComponent.key(row);

      if (this.hiddenLines.includes(line)) {
        return false;
      }
      if (this.alwaysShownLines.includes(line)) {
        return true;
      }
      // Hide lines that don't apply to this attorney (all displayed columns are zero).
      return !this.isAllZero(row);
    });
  }

  private isAllZero(row: CLESummaryRowDTO): boolean {
    const values = this.showInHouse
      ? [row.regular, row.inHouse, row.ethics, row.prof]
      : [row.regular, row.ethics, row.prof];
    return values.every(v => !v);
  }

  /**
   * Emphasises what the member still owes.
   *
   * ⚠ THIS NEVER FIRED. It tested `summaryLine.includes('still needed')`, and there is no such line —
   * the backend constant is "Hours Needed" and "still needed" appears only in the DESCRIPTION text.
   * So the highlight has been dead, and the row it exists to draw attention to has rendered like any
   * other. Same stale-vocabulary defect recorded in feedback_cle_fe_summaryline_vocabulary.md.
   *
   * ⚠ AND IT MUST NOT FIRE ON AN OPEN PERIOD. "Hours Needed" means REMAINING while the period runs,
   * not owed: the 2026-2027 biennium does not close until 2027-12-31, so highlighting it would tell
   * every attorney they are deficient from day one — the exact failure the backend guards against by
   * emitting no verdict for an open period. A shortfall carried from an EARLIER period is genuinely
   * past due and is highlighted either way.
   */
  isHighlightRow(row: CLESummaryRowDTO): boolean {
    const line = CleSummaryTableComponent.key(row);

    if (line === CleSummaryTableComponent.LINE.neededPrevYear) {
      return true;
    }

    return line === CleSummaryTableComponent.LINE.needed && !this.periodIsOpen;
  }
}
